import { useState } from 'react';

const Classes = () => {
  const [selectedDivision, setSelectedDivision] = useState('all');

  const classPrograms = [
    {
      id: 1,
      division: 'children',
      title: 'Pre-Ballet (Ages 3-4)',
      description: 'Introduction to movement, rhythm, and basic ballet positions through creative play and imagination.',
      duration: '30 minutes',
      schedule: 'Saturdays 9:00-9:30 AM',
      price: '$60/month',
      features: [
        'Creative movement exploration',
        'Basic ballet positions',
        'Rhythm and musicality',
        'Social interaction skills',
        'Parent observation welcome'
      ]
    },
    {
      id: 2,
      division: 'children',
      title: 'Primary Ballet (Ages 5-6)',
      description: 'Structured introduction to ballet technique with emphasis on following directions and classroom etiquette.',
      duration: '45 minutes',
      schedule: 'Saturdays 10:00-10:45 AM',
      price: '$75/month',
      features: [
        'Basic ballet positions',
        'Simple combinations',
        'Classroom discipline',
        'Following instructions',
        'Beginning flexibility'
      ]
    },
    {
      id: 3,
      division: 'youth',
      title: 'Elementary Ballet (Ages 7-9)',
      description: 'Foundation building with proper ballet technique, terminology, and beginning center work.',
      duration: '60 minutes',
      schedule: 'Tuesdays & Thursdays 4:00-5:00 PM',
      price: '$120/month',
      features: [
        'Proper ballet technique',
        'French terminology',
        'Barre and center work',
        'Basic jumps and turns',
        'Performance opportunities'
      ]
    },
    {
      id: 4,
      division: 'youth',
      title: 'Intermediate Ballet (Ages 10-12)',
      description: 'Advanced technique development with introduction to pointe preparation and performance skills.',
      duration: '75 minutes',
      schedule: 'Monday, Wednesday, Friday 4:30-5:45 PM',
      price: '$180/month',
      features: [
        'Advanced technique',
        'Pointe preparation',
        'Complex combinations',
        'Performance training',
        'Competition opportunities'
      ]
    },
    {
      id: 5,
      division: 'pre-professional',
      title: 'Advanced Ballet (Ages 13-15)',
      description: 'Intensive training for serious ballet students with pointe work and advanced repertoire.',
      duration: '90 minutes',
      schedule: 'Monday-Friday 5:00-6:30 PM',
      price: '$250/month',
      features: [
        'Pointe technique',
        'Classical variations',
        'Advanced allegro',
        'Performance roles',
        'Master class access'
      ]
    },
    {
      id: 6,
      division: 'pre-professional',
      title: 'Pre-Professional Program (Ages 16-18)',
      description: 'Comprehensive training program for students pursuing professional dance careers.',
      duration: '2 hours',
      schedule: 'Monday-Saturday 6:00-8:00 PM',
      price: '$350/month',
      features: [
        'Professional-level training',
        'Daily pointe classes',
        'Repertoire coaching',
        'Audition preparation',
        'Career guidance'
      ]
    },
    {
      id: 7,
      division: 'adult',
      title: 'Adult Beginner Ballet',
      description: 'Perfect for adults with no prior ballet experience who want to learn proper technique.',
      duration: '60 minutes',
      schedule: 'Tuesdays & Thursdays 7:00-8:00 PM',
      price: '$100/month',
      features: [
        'Beginner-friendly instruction',
        'Proper technique foundation',
        'Supportive environment',
        'Flexibility improvement',
        'Stress relief through dance'
      ]
    },
    {
      id: 8,
      division: 'adult',
      title: 'Adult Intermediate Ballet',
      description: 'For adults with some ballet background looking to advance their technique.',
      duration: '75 minutes',
      schedule: 'Mondays & Wednesdays 7:30-8:45 PM',
      price: '$120/month',
      features: [
        'Intermediate technique',
        'Complex combinations',
        'Pointe work (optional)',
        'Performance opportunities',
        'Adult-focused instruction'
      ]
    },
    {
      id: 9,
      division: 'specialty',
      title: 'Jazz Dance',
      description: 'High-energy classes focusing on jazz technique, style, and performance.',
      duration: '60 minutes',
      schedule: 'Saturdays 11:00 AM-12:00 PM',
      price: '$90/month',
      features: [
        'Jazz technique',
        'Contemporary styles',
        'Performance training',
        'Musicality development',
        'Cross-training benefits'
      ]
    },
    {
      id: 10,
      division: 'specialty',
      title: 'Contemporary Dance',
      description: 'Expressive movement combining ballet, modern, and jazz techniques.',
      duration: '75 minutes',
      schedule: 'Fridays 6:00-7:15 PM',
      price: '$110/month',
      features: [
        'Contemporary technique',
        'Floor work',
        'Improvisation',
        'Emotional expression',
        'Choreographic exploration'
      ]
    }
  ];

  const divisions = [
    { id: 'all', label: 'All Programs' },
    { id: 'children', label: 'Children (Ages 3-6)' },
    { id: 'youth', label: 'Youth (Ages 7-12)' },
    { id: 'pre-professional', label: 'Pre-Professional (Ages 13-18)' },
    { id: 'adult', label: 'Adult Programs' },
    { id: 'specialty', label: 'Specialty Classes' }
  ];

  const filteredPrograms = selectedDivision === 'all' 
    ? classPrograms 
    : classPrograms.filter(program => program.division === selectedDivision);

  return (
    <main className="min-h-screen bg-ballet-light">
      {/* Hero Section */}
      <div className="bg-elegant-gradient text-white py-20">
        <div className="container-custom text-center">
          <h1 className="text-5xl lg:text-6xl font-elegant font-bold mb-6">
            Classes & Programs
          </h1>
          <p className="text-xl lg:text-2xl text-white/90 max-w-3xl mx-auto mb-8">
            Comprehensive ballet education for all ages and skill levels
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button className="btn-primary bg-white text-ballet-burgundy hover:bg-ballet-light">
              Register Now
            </button>
            <button className="btn-outline border-white text-white hover:bg-white hover:text-ballet-burgundy">
              Schedule Trial Class
            </button>
          </div>
        </div>
      </div>

      <div className="container-custom section-padding">
        {/* Division Filter */}
        <div className="flex flex-wrap justify-center mb-12 bg-white rounded-2xl p-2 shadow-elegant">
          {divisions.map((division) => (
            <button
              key={division.id}
              onClick={() => setSelectedDivision(division.id)}
              className={`px-4 py-3 rounded-xl font-semibold transition-all duration-300 text-sm lg:text-base ${
                selectedDivision === division.id
                  ? 'bg-ballet-burgundy text-white shadow-lg'
                  : 'text-ballet-burgundy hover:bg-ballet-light'
              }`}
            >
              {division.label}
            </button>
          ))}
        </div>

        {/* Programs Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-16">
          {filteredPrograms.map((program) => (
            <div key={program.id} className="card-elevated">
              <div className="p-8">
                <div className="flex justify-between items-start mb-4">
                  <h3 className="text-2xl font-elegant font-bold text-ballet-burgundy">
                    {program.title}
                  </h3>
                  <span className="bg-ballet-light text-ballet-burgundy px-3 py-1 rounded-full text-sm font-semibold">
                    {program.price}
                  </span>
                </div>
                
                <p className="text-gray-700 mb-6">{program.description}</p>
                
                <div className="grid grid-cols-2 gap-4 mb-6">
                  <div>
                    <h4 className="font-semibold text-ballet-burgundy mb-2">Duration:</h4>
                    <p className="text-gray-700">{program.duration}</p>
                  </div>
                  <div>
                    <h4 className="font-semibold text-ballet-burgundy mb-2">Schedule:</h4>
                    <p className="text-gray-700">{program.schedule}</p>
                  </div>
                </div>
                
                <div className="mb-6">
                  <h4 className="font-semibold text-ballet-burgundy mb-3">What You'll Learn:</h4>
                  <ul className="space-y-2">
                    {program.features.map((feature, index) => (
                      <li key={index} className="flex items-center text-gray-700">
                        <svg className="w-4 h-4 text-ballet-burgundy mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                        </svg>
                        {feature}
                      </li>
                    ))}
                  </ul>
                </div>
                
                <div className="flex gap-3">
                  <button className="flex-1 btn-primary">
                    Register
                  </button>
                  <button className="flex-1 btn-outline">
                    Trial Class
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Additional Information */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Registration Information */}
          <div className="card-elevated">
            <div className="p-8">
              <h3 className="text-2xl font-elegant font-bold text-ballet-burgundy mb-6">
                Registration Information
              </h3>
              
              <div className="space-y-4">
                <div className="bg-ballet-light p-4 rounded-xl">
                  <h4 className="font-semibold text-ballet-burgundy mb-2">Registration Periods:</h4>
                  <p className="text-gray-700 text-sm">
                    Fall Semester: August 1-15<br/>
                    Spring Semester: January 1-15<br/>
                    Summer Intensive: May 1-15
                  </p>
                </div>
                
                <div className="bg-ballet-light p-4 rounded-xl">
                  <h4 className="font-semibold text-ballet-burgundy mb-2">What to Bring:</h4>
                  <p className="text-gray-700 text-sm">
                    • Comfortable, fitted clothing<br/>
                    • Ballet shoes (we can recommend brands)<br/>
                    • Hair tied back securely<br/>
                    • Water bottle<br/>
                    • Positive attitude!
                  </p>
                </div>
                
                <div className="bg-ballet-light p-4 rounded-xl">
                  <h4 className="font-semibold text-ballet-burgundy mb-2">Trial Classes:</h4>
                  <p className="text-gray-700 text-sm">
                    First-time students can try any class for just $15. 
                    This allows you to experience our teaching style and 
                    determine the best fit for your goals.
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Contact & Support */}
          <div className="card-elevated">
            <div className="p-8">
              <h3 className="text-2xl font-elegant font-bold text-ballet-burgundy mb-6">
                Need Help Choosing?
              </h3>
              
              <p className="text-gray-700 mb-6">
                Our experienced faculty can help you find the perfect class for your 
                age, skill level, and goals. Contact us for a consultation or to 
                schedule a trial class.
              </p>
              

              
              <div className="flex flex-col gap-3">
                <button className="btn-outline">
                  View Class Calendar
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </main>
  );
};

export default Classes;
